import { COLORS, APP_NAME } from '@strata/config';

export interface LogoProps {
  /** When true, render the wordmark for a dark surface (e.g. the navy sidebar). */
  on_dark?: boolean;
  /** Pixel height of the mark; wordmark scales with it. */
  height?: number;
  className?: string;
}

/**
 * Strata logo (CLAUDE.md §2): three stacked "strata" bars (gold / white / cyan)
 * plus the wordmark. SVG so it stays crisp; `on_dark` swaps the wordmark fill.
 *
 * TODO(spec): bar geometry/proportions are derived; replace with the supplied
 * SVG asset once provided.
 */
export function Logo({ on_dark = false, height = 28, className }: LogoProps) {
  const wordFill = on_dark ? COLORS['ink-text'] : COLORS.text;
  const middleBar = on_dark ? COLORS.surface : COLORS.ink;
  // A name with a " - " separator (e.g. "LTM Energy - Goldfields Cockpit") renders as a stacked
  // two-line wordmark — brand on top, product beneath — so it fits the 230px sidebar; a plain name
  // keeps the original single 22px line.
  const parts = APP_NAME.split(' - ');
  const twoLine = parts.length === 2;
  const vbWidth = twoLine ? 232 : 184;
  const width = height * (vbWidth / 40);

  return (
    <svg
      role="img"
      aria-label={APP_NAME}
      width={width}
      height={height}
      viewBox={`0 0 ${vbWidth} 40`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-on-dark={on_dark ? 'true' : 'false'}
    >
      {/* three stacked strata bars, offset to suggest geological layers */}
      <rect x="2" y="6" width="30" height="7" rx="2" fill={COLORS.gold} />
      <rect x="6" y="16.5" width="30" height="7" rx="2" fill={middleBar} />
      <rect x="2" y="27" width="30" height="7" rx="2" fill={COLORS.cyan} />
      {twoLine ? (
        <text x="46" fontFamily="Inter, sans-serif" fill={wordFill}>
          <tspan x="46" y="19" fontSize="16" fontWeight="700" letterSpacing="-0.3">
            {parts[0]}
          </tspan>
          <tspan x="46" y="34" fontSize="12.5" fontWeight="500" letterSpacing="-0.2" opacity="0.85">
            {parts[1]}
          </tspan>
        </text>
      ) : (
        <text
          x="46"
          y="28"
          fontFamily="Inter, sans-serif"
          fontSize="22"
          fontWeight="700"
          letterSpacing="-0.5"
          fill={wordFill}
        >
          {APP_NAME}
        </text>
      )}
    </svg>
  );
}
